import { css } from '@styled-system/css';
import { type FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type VestiaireFormErrors,
  hasBlockingVestiaireError,
  hasNarrowingCriterion,
} from '@/features/productSearch/vestiaire/vestiaireCriteria';
import type { VestiaireSearchCriteriaInput } from '@/shared/api/types/generated';
import { Spinner } from '@/shared/components/ui/Spinner';
import { usePreviewVestiaireMatchCountQuery } from '@/shared/hooks/queries/generated';
import { matchPreviewPrefix } from '@/shared/utils/analysis/similarity';

// ============================================================================
// Aperçu du volume Vestiaire (previewVestiaireMatchCount). Appel coûteux (jusqu'à
// 126 requêtes SQL) : débouncé à 1 s sur le critère SÉRIALISÉ. Rendu IDENTIQUE à l'aperçu Vinted
// de SearchModal (même chiffre, même préfixe, même libellé) : le comptage VC étant exact, c'est le cas
// « exhaustif » de Vinted — `sampledCount` suivi de « articles analysés », « + » si échantillonné ;
// source de similarité fournie (`similarityPending`) : « ≤ » et le libellé Vinted correspondant.
// ============================================================================

const PREVIEW_DEBOUNCE_MS = 1000;

const mutedText = css({ fontSize: 'caption', color: 'text.tertiary' });

// Mêmes styles que le chiffre de l'aperçu Vinted (SearchModal) — les garder alignés.
const countText = css({
  fontFamily: 'mono',
  fontSize: '17px',
  lineHeight: '1.15',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.4px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3',
});

const countLabel = css({ fontSize: 'bodySm', fontWeight: 'medium', color: 'text.secondary' });

export interface VestiaireMatchPreviewProps {
  criteria: VestiaireSearchCriteriaInput;
  /** Erreurs du formulaire : un critère invalide ne part JAMAIS (400 assuré, appel coûteux). */
  errors: VestiaireFormErrors;
  enabled: boolean;
}

export const VestiaireMatchPreview: FC<VestiaireMatchPreviewProps> = ({ criteria, errors, enabled }) => {
  const { t, i18n } = useTranslation();
  const serialized = JSON.stringify(criteria);

  // Le critère retenu porte sa clé sérialisée : un nouvel objet de même contenu ne relance rien.
  const [debounced, setDebounced] = useState<{ key: string; criteria: VestiaireSearchCriteriaInput } | null>(null);
  const debouncedKey = debounced?.key;
  // Critère le plus récent lu par référence : le minuteur ne dépend que de la clé sérialisée, un
  // parent qui recrée l'objet à chaque rendu ne le relance donc jamais.
  const latestCriteria = useRef(criteria);
  useEffect(() => {
    latestCriteria.current = criteria;
  }, [criteria]);
  useEffect(() => {
    if (debouncedKey === serialized) return;
    const handle = window.setTimeout(
      () => setDebounced({ key: serialized, criteria: latestCriteria.current }),
      PREVIEW_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [serialized, debouncedKey]);

  const input = debounced?.criteria ?? {};
  const narrowing = hasNarrowingCriterion(input);
  // Bornes serveur dépassées ou prix invalide : ne PAS lancer l'aperçu (appel coûteux, 400 assuré).
  // Comme Vinted, aucun message dédié n'est affiché ici : le rendu ci-dessous reste dans son état
  // « en attente » (spinner), exactement ce que Vinted montre lui aussi en cas d'erreur.
  const invalid = hasBlockingVestiaireError(errors);
  // isFetching (comme Vinted) : chaque nouveau critère débouncé repasse par le spinner, même en
  // rafraîchissement — cf SearchModal.usePreviewAnalysisMatchCountQuery.
  const { data, isFetching } = usePreviewVestiaireMatchCountQuery(
    { input },
    {
      enabled: enabled && debounced != null && debouncedKey === serialized && narrowing && !invalid,
      staleTime: 60_000,
      placeholderData: (previous) => previous,
    },
  );

  if (!enabled) return null;

  if (!hasNarrowingCriterion(criteria)) {
    return <span className={mutedText}>{t('searchModal.previewScopeHint')}</span>;
  }

  const preview = data?.previewVestiaireMatchCount;
  // Même condition que Vinted (matchPreview == null) : tant qu'aucune réponse n'est là — en attente,
  // invalide (aperçu jamais lancé) ou en échec —, le spinner reste affiché indéfiniment.
  const pending = debouncedKey !== serialized || isFetching || preview == null;

  return (
    <span className={countText}>
      {pending ? (
        <Spinner size={14} />
      ) : (
        // similarityPending : l'aperçu n'applique jamais la similarité -> le nombre est un
        // MAJORANT (« ≤ »), exactement comme l'aperçu Vinted
        `${matchPreviewPrefix(preview)}${preview.sampledCount.toLocaleString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR')}`
      )}
      {!pending && (
        <span className={countLabel}>
          {preview.similarityPending ? t('searchModal.previewBeforeSimilarity') : t('searchModal.previewAnalysed')}
        </span>
      )}
    </span>
  );
};
